import { RECOMMENDATION_PRODUCT_TYPE_UPSELLS } from '@bluheadless/ui-logic/src/constants'
import { useProductRecommendations } from '@bluheadless/ui-logic/src/hooks/product/useProductRecommendations'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { usePageContext } from '@bluheadless/ui-logic/src/providers/page/page'
import { useProductContext } from '@bluheadless/ui-logic/src/providers/product'
import { slidesPerViewForBreakpoints } from '@bluheadless/ui-logic/src/utils/commons'
import { cx } from '@emotion/css'
import { useTheme, useThemeProps } from '@mui/system'
import round from 'lodash/round'
import { forwardRef, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import SkeletonCard from '../../molecules/skeleton/card/skeleton-card'
import TileConnected from '../../molecules/tile/connected/tile-connected'
import { Slide } from '../../particles/slider'
import Carousel from '../carousel'
import { Root, Title } from './upsells.styled'

const WIDGET_CONTEXT = 'up-sells'

const Upsells = forwardRef(({ className, ...inProps }, ref) => {
	const props = useThemeProps({
		name: 'BHUpsells',
		props: inProps,
	})

	const { skeletonProps, sliderProps, showTitle, tileProps } = props

	const theme = useTheme()

	const productContextValue = useProductContext()

	const { entityType: pageType, __bhEntityId: pageId } = usePageContext()

	const [{ product: { upsells: upsellsFallback, sku } } = {}] = productContextValue

	const { products: upsells, loading } = useProductRecommendations({
		type: RECOMMENDATION_PRODUCT_TYPE_UPSELLS,
		sku,
		options: { fallbackData: upsellsFallback },
		pageContext: pageType,
		pageId,
		widgetContext: WIDGET_CONTEXT,
	})

	const {
		catalog: {
			products: {
				images: { width: productImageWidth, height: productImageHeight },
				upsells: {
					slider: { slidesPerView: _slidesPerView },
				},
			},
		},
	} = useConfig()

	const slidesPerView = slidesPerViewForBreakpoints(_slidesPerView, theme.breakpoints)
	const cardRatio = productImageWidth / productImageHeight
	const responsiveProps = Object.entries(_slidesPerView)
		.map(([breakpoint, value]) => [
			breakpoint,
			{
				columns: round(parseFloat(value)),
				columnGap: skeletonProps.columnGap + 'px',
			},
		])
		.reduce((acc, [breakpoint, value]) => {
			return { ...acc, [breakpoint]: value }
		}, {})

	const breakpointsMerged = Object.entries(sliderProps.breakpoints)
		.map(([breakpoint, value]) => [
			theme.breakpoints.values[breakpoint],
			{
				...value,
				slidesPerView: _slidesPerView[breakpoint],
			},
		])
		.reduce((acc, [breakpoint, value]) => {
			return { ...acc, [breakpoint]: value }
		}, {})

	const titleComponent = useMemo(
		() =>
			showTitle ? (
				<Title component="div" variant="h3">
					<FormattedMessage id="upsells_title" />
				</Title>
			) : null,
		[showTitle]
	)

	return upsells?.length > 0 ? (
		<Root ref={ref} className={cx('Upsells-root', className)} {...props}>
			{loading ? (
				<SkeletonCard cardRatio={cardRatio} responsiveProps={responsiveProps} />
			) : (
				<Carousel
					{...sliderProps}
					breakpoints={breakpointsMerged}
					titleComponent={titleComponent}
					slidesPerView={slidesPerView[theme.breakpoints.values['xs']]}
				>
					{upsells.map((product, index) => (
						<Slide key={index}>
							<TileConnected
								position={index}
								product={product}
								title1Props={{ component: 'h4' }}
								widgetContext={WIDGET_CONTEXT}
								{...tileProps}
							/>
						</Slide>
					))}
				</Carousel>
			)}
		</Root>
	) : null
})

Upsells.displayName = 'Upsells'

export default Upsells
